import { ReactNode } from "react";
import { clsx } from "clsx";
import { useLocation } from "react-router-dom";
import { Logo } from "@/common/Logo/Logo.tsx";
import { AUTH_ROUTES } from "./authHelpers.ts";
import {
    LoginShowcasePanel,
    SignupTestimonialPanel,
} from "./components/RightPanels.tsx";
import { useAuthSurface } from "./authSurface.ts";

const HOME_URL = `https://www.${import.meta.env.VITE_DOMAIN}`;

/**
 * Two-column marketing auth layout: the form on the left (white), a brand panel
 * on the right that varies by flow — a testimonial + "trusted by" logos on the
 * sign-up path, a feature showcase everywhere else. The right panel is hidden
 * below `lg`. Matches the new website design system — it does not follow the
 * app's zinc/indigo conventions on purpose.
 *
 * The admin surface drops the right panel and centers the form -- there is
 * nobody to market to behind a basic-auth prompt. So does a screen that is a
 * step in something the visitor is already doing, such as the consent screen
 * an app sends a signed-in user to (`showcase={false}`).
 */
export default function AuthShell({
    children,
    showcase = true,
    rightPanel,
}: {
    children: ReactNode;
    showcase?: boolean;
    /**
     * Stands in for the brand panel on a screen whose right half is part of
     * the offer, such as the drafted profile on an audit link.
     */
    rightPanel?: ReactNode;
}) {
    const { pathname } = useLocation();
    const marketing = useAuthSurface().marketing && showcase;
    const isSignUp =
        pathname.startsWith(AUTH_ROUTES.signUp) ||
        pathname === AUTH_ROUTES.confirmSignUp;

    return (
        <div className="flex min-h-dvh bg-white font-sans text-brand-purple">
            <div
                className={clsx(
                    "flex w-full flex-col px-6 py-10 lg:px-16 xl:px-24",
                    marketing && "lg:w-1/2",
                )}
            >
                {/* Logo pinned to the top of the form column, left-aligned with
                    the form; the form itself is vertically centered below. */}
                <div className="mx-auto flex w-full max-w-sm flex-1 flex-col">
                    <a
                        href={HOME_URL}
                        aria-label="Spectacle home"
                        className="inline-block shrink-0"
                    >
                        <Logo variant="color+primary" className="h-6 w-auto" />
                    </a>
                    <div className="flex flex-1 flex-col justify-center gap-10 py-12">
                        {children}
                    </div>
                </div>
            </div>

            {marketing &&
                (rightPanel ??
                    (isSignUp ? (
                        <SignupTestimonialPanel />
                    ) : (
                        <LoginShowcasePanel />
                    )))}
        </div>
    );
}
